<template>
  <div
    class="fu-task"
    :class="{ 'fu-task--grid': !!gridTemplate, 'fu-task--completing': completing }"
    :style="gridTemplate ? { gridTemplateColumns: gridTemplate } : null"
    data-test="fu-task-row"
    :data-task-id="task.id"
    tabindex="0"
    :aria-label="`Open task: ${text || 'Untitled task'}`"
    @click="open"
    @keydown.enter.self.prevent="open"
    @keydown.space.self.prevent="open"
  >
    <div class="fu-task__check-cell">
      <button
        type="button"
        class="fu-task__check"
        :class="{ 'is-checked': completing }"
        :disabled="completing"
        :title="completing ? 'Completing…' : 'Mark complete'"
        :aria-label="completing ? 'Completing task' : `Mark complete: ${text || 'task'}`"
        data-test="fu-task-complete"
        @click.stop="complete"
      >
        <fu-icon name="check" :size="12" :stroke="3" />
      </button>
    </div>

    <div class="fu-task__main">
      <button type="button" class="fu-task__text" data-test="fu-task-open" @click.stop="open">
        {{ text || 'Untitled task' }}
      </button>
      <span
        v-for="(p, i) in patients"
        :key="`p-${p.id || i}`"
        class="fu-task__patient"
        data-test="fu-task-patient"
        @click.stop
      >
        <span class="fu-task__patient-init" aria-hidden="true">{{ p.initials }}</span>{{ p.name }}
      </span>
      <span v-for="tag in tags" :key="`t-${tag}`" class="fu-task__tag" data-test="fu-task-tag">
        <svg class="fu-task__tag-icon" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false">
          <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
          <circle cx="7.5" cy="7.5" r="1.2" />
        </svg>{{ tag }}
      </span>
      <span v-if="isNew" class="fu-task__new" data-test="fu-task-new">New today</span>
    </div>

    <div class="fu-task__meta">
      <span v-if="fromText" class="fu-task__from" data-test="fu-task-from">{{ fromText }}</span>
      <span v-if="repeatText" class="fu-task__repeat" data-test="fu-task-repeat">
        <fu-icon name="restart" :size="13" />{{ repeatText }}
      </span>
    </div>

    <div class="fu-task__people">
      <span
        v-for="p in people"
        :key="p.id"
        class="fu-task__avatar"
        :title="p.name"
        data-test="fu-task-assignee"
      >{{ p.initials }}</span>
    </div>
  </div>
</template>

<script>
/**
 * AdminTaskRow — one task in the Admin "Today" worklist's "Your tasks" section (Admin redesign
 * Phase A1, 2026-10-06; design "Today 3a"). B1 replaces it with the Tasks page's TaskRow
 * (`compact`), see the plan.
 *
 *   <admin-task-row :task="t" :grid-template="…" @open="openTask" @complete="…" />
 *
 * Shows: the round check, the text (HTML stripped, mention / patient / tag badges pulled out —
 * TodoItem.cleanContent), the patient chips, the task's tags, "New today" (created today, local
 * date — TodoItem.isCreatedToday), "From <creator>" when someone else created it, the repeat
 * frequency, and the assignees' initials when the task is not only the user's.
 *
 * The check COMPLETES the task exactly as the Tasks list's checkbox does (TodoItem.changeStatus +
 * the page's handleItemChange): for a repeating task due today or later, tasks/addPersonalTask
 * creates the next instance unless it already exists; then tasks/updateTask saves the completed
 * task. The payloads are built by src/utils/tasks/completeTask.js (pinned to TodoItem.vue by
 * cypress/node-tests/tasks-complete-task.test.js). Like TodoItem, the update waits
 * `completeDelay` ms (1200) so the tick is seen before the row leaves the list. Emits
 * `complete(change)` after the update, `open(task)` when the ROW is clicked (anywhere — the text
 * button `fu-task-open` too) or Enter / Space is pressed on the focused row, as the Tasks page's
 * TaskRow.vue does. The check and the patient chips stop the click (they never open the drawer).
 *
 * Reads the user and the employees from the store (state.customer — what src/mixin.js reads),
 * without the mixin. Never reads $route.
 */
import moment from 'moment-timezone';
import FuIcon from './FuIcon.vue';
import { initialsOf } from '@/utils/followups/rowModel.js';
import {
  cleanTaskContent, combineStoreTasks, needsNextInstance, findNextInstance, nextInstancePayload, completedTaskPayload,
} from '@/utils/tasks/completeTask.js';

const SAVE_ERROR_TEXT = 'The task could not be completed. Please try again in a moment.';

/** "Maya Rivera" → "MR"; "Front Desk Team" → "FT". */
function nameInitials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = words[0][0] || '';
  const last = words.length > 1 ? (words[words.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

/** "Saumil Shah" → "Saumil S." */
function shortName(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  return words.length > 1 ? `${words[0]} ${words[words.length - 1][0].toUpperCase()}.` : words[0];
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default {
  name: 'AdminTaskRow',
  components: { FuIcon },
  props: {
    task: { type: Object, required: true },
    // The worklist's grid (its column widths) so the row lines up with the follow-up rows; null
    // draws a flex row (tablet / phone).
    gridTemplate: { type: String, default: null },
    // TodoItem's 1200ms between the tick and the save. Specs pass 0.
    completeDelay: { type: Number, default: 1200 },
  },

  data() {
    return { completing: false };
  },

  computed: {
    currentUserId() {
      const c = this.$store && this.$store.state.customer;
      return c ? c.userID : null;
    },
    employeeList() {
      const c = this.$store && this.$store.state.customer;
      return c && Array.isArray(c.employees) ? c.employees : [];
    },
    cleaned() {
      return cleanTaskContent(this.task.content, typeof document !== 'undefined' ? document : null);
    },
    text() {
      return this.cleaned.text;
    },
    patients() {
      return this.cleaned.patients.map(p => ({ id: p.id, name: p.name, initials: initialsOf(p.name) }));
    },
    tags() {
      return Array.isArray(this.task.tags) ? this.task.tags.filter(Boolean).map(String) : [];
    },
    isNew() {
      const created = toDate(this.task.createdAt);
      if (!created) return false;
      const today = new Date();
      return created.getFullYear() === today.getFullYear()
        && created.getMonth() === today.getMonth()
        && created.getDate() === today.getDate();
    },
    fromText() {
      const by = this.task.createdByEmpID;
      if (by === null || by === undefined || by === '') return '';
      if (String(by) === String(this.currentUserId)) return '';
      const name = this.employeeName(by);
      return name ? `From ${shortName(name)}` : '';
    },
    repeatText() {
      return this.task.repeat && this.task.repeatFrequency ? String(this.task.repeatFrequency) : '';
    },
    // Assignees (TodoItem: `assignees`, else the single assignedToEmpID) — shown only when the task
    // is not just the user's own.
    people() {
      const ids = Array.isArray(this.task.assignees) && this.task.assignees.length
        ? this.task.assignees
        : [this.task.assignedToEmpID];
      const list = ids.filter(id => id !== null && id !== undefined && id !== '').map(String);
      const unique = list.filter((id, i) => list.indexOf(id) === i);
      if (!unique.some(id => id !== String(this.currentUserId))) return [];
      return unique.slice(0, 3).map((id) => {
        const name = this.employeeName(id);
        return { id, name: name || 'Unknown', initials: nameInitials(name) || '?' };
      });
    },
    storeTasks() {
      const g = (this.$store && this.$store.getters) || {};
      return combineStoreTasks(g['tasks/myCreatedTasks'], g['tasks/assignedToMeTasks'], g['tasks/myEmployeesTasks']);
    },
  },

  methods: {
    employeeName(id) {
      const e = this.employeeList.find(x => String(x.EmployeeID) === String(id));
      return e ? e.Name : '';
    },

    /** The whole row, the text, Enter / Space on the focused row (TaskRow.vue's pattern). */
    open() {
      this.$emit('open', this.task);
    },

    textOf(content) {
      return cleanTaskContent(content, typeof document !== 'undefined' ? document : null).text;
    },

    wait(ms) {
      return ms > 0 ? new Promise(resolve => setTimeout(resolve, ms)) : Promise.resolve();
    },

    async complete() {
      const item = this.task;
      if (this.completing || !item || item.status !== 'INCOMPLETE') return;
      this.completing = true;
      try {
        const dueAt = moment(item.dueAt).toDate();
        const dueAtStr = moment(item.dueAt).format('MM/DD/YYYY');
        const completedAt = moment().toDate();
        const completedAtStr = moment().format('MM/DD/YYYY');
        const diff = moment(item.dueAtStr).diff(moment(), 'days');

        if (needsNextInstance(item, diff)) {
          const nextDueAt = this.$store.getters['tasks/getNextDueDate'](item).toDate();
          const nextDueAtStr = moment(nextDueAt).format('MM/DD/YYYY');
          const existing = findNextInstance(item, this.storeTasks, nextDueAtStr, this.textOf);
          if (!existing) {
            await this.$store.dispatch('tasks/addPersonalTask', nextInstancePayload(item, {
              createdAt: moment().toDate(),
              nextDueAt,
              nextDueAtStr,
            }));
          }
        }

        const change = completedTaskPayload(item, {
          dueAt,
          dueAtStr,
          createdAt: moment(item.createdAt).toDate(),
          completedAt,
          completedAtStr,
          completedByEmpID: this.currentUserId,
        });
        await this.wait(this.completeDelay);
        await this.$store.dispatch('tasks/updateTask', change);
        this.$emit('complete', change);
      } catch (err) {
        console.error('[AdminTaskRow] completing the task failed:', err);
        this.completing = false;
        if (this.$toast) this.$toast.error(SAVE_ERROR_TEXT);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-task {
  display: flex;
  align-items: center;
  gap: $fu-space-3;
  padding: 10px 18px;
  border-bottom: 1px solid $fu-border;
  min-width: 0;
  cursor: pointer;
  transition: background $fu-dur-fast $fu-ease-out;
  &:hover { background: $fu-neutral-25; }
  &:focus { outline: none; }
  &:focus-visible { box-shadow: inset 0 0 0 2px $fu-brand; }
}

.fu-task--grid {
  display: grid;
  .fu-task__main { grid-column: 2 / -3; }
  .fu-task__meta { grid-column: -3 / -2; }
  .fu-task__people { grid-column: -2 / -1; }
}

.fu-task__check-cell {
  width: 28px;
  display: flex;
  justify-content: center;
  flex: none;
}

.fu-task__check {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid $fu-neutral-300;
  background: $fu-surface;
  color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background $fu-dur-fast $fu-ease-out, border-color $fu-dur-fast $fu-ease-out;
  &:hover:not([disabled]) { border-color: $fu-brand; color: $fu-brand; }
  &.is-checked {
    border-color: $fu-brand;
    background: $fu-brand;
    color: #ffffff;
    cursor: default;
  }
}

.fu-task__main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px $fu-space-2;
}

.fu-task__text {
  @include fu-button-reset;
  @include fu-focus-ring;
  min-width: 0;
  max-width: 100%;
  border-radius: $fu-radius-xs;
  text-align: left;
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  overflow-wrap: anywhere;
  &:hover { text-decoration: underline; }
}

.fu-task--completing .fu-task__text {
  color: $fu-text-subtle;
  text-decoration: line-through;
}

.fu-task__patient {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  max-width: 100%;
  padding: 0 10px 0 3px;
  border-radius: $fu-radius-pill;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fu-task__patient-init {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: $fu-brand-100;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 8.5px;
  font-weight: 800;
  flex: none;
}

.fu-task__tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: $fu-radius-xs + 2px;
  border: 1px solid $fu-border;
  color: $fu-neutral-600;
  font-size: 12px;
  font-weight: $fu-fw-semibold;
  white-space: nowrap;
}

.fu-task__tag-icon {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

.fu-task__new {
  @include fu-pill($fu-brand-50, $fu-brand-700);
}

.fu-task__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px $fu-space-3;
  min-width: 0;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.fu-task__from,
.fu-task__repeat {
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.fu-task__people {
  display: flex;
  align-items: center;
  flex: none;
}

.fu-task__avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  border: 2px solid $fu-surface;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: $fu-fw-bold;
  & + & { margin-left: -6px; }
}

@include fu-phone {
  .fu-task {
    flex-wrap: wrap;
    padding: 12px $fu-space-4;
  }
  .fu-task__main { flex-basis: calc(100% - 44px); }
  .fu-task__meta { padding-left: 40px; }
}
</style>
